/** @jsxRuntime classic */
/** @jsx jsx */
import { memo, useRef, forwardRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import theme from "../../theme/settings";
const levels = [0, 1, 'second', 'third', 'fourth', 'fifth'];
const List = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  className,
  width,
  maxWidth,
  children,
  style,
  scrollAble,
  maxHeight,
  level,
  refs,
  ...props
}, ref) => {
  if (!ref) {
    ref = useRef(null);
  }
  const ListRoot = css`
            display: block;
            position: relative;
            width: ${isNaN(width) ? width : width + 'px'};
            max-width: ${isNaN(maxWidth) ? maxWidth : maxWidth + 'px'};
            height: auto;
            padding: 5px 0;
            background-color: #fff;
            box-sizing: border-box;
            &.scroll-able {
                max-height: ${maxHeight ? isNaN(maxHeight) ? maxHeight : maxHeight + 'px' : '300px'};
                overflow-y: auto;
                padding-top: 0;
                &::-webkit-scrollbar {
                    width: 6px;
                    background-color: white;
                    border-radius: 4px;
                }
                &::-webkit-scrollbar-thumb {
                    border-radius: 10px;
                    mix-blend-mode: normal;
                    background-color: ${theme.colors.system.disabled};
                    background-clip: content-box;
                }
            }
            .second {
                padding: 0;
                .DGN-UI-List-Item {
                    padding-left: 32px;
                }
            }
            .third {
                padding: 0;
                .DGN-UI-List-Item {
                    padding-left: 48px;
                }
            }
            .fourth {
                padding: 0;
                .DGN-UI-List-Item {
                    padding-left: 64px;
                }
            }
            .fifth {
                padding: 0;
                .DGN-UI-List-Item {
                    padding-left: 80px;
                }
            }
        `;
  useEffect(() => {
    if (refs) refs(ref);
  }, []);
  useEffect(() => {
    if (level && level > 1 && level < levels.length) {
      ref.current.classList.add(levels[level]);
    }
    return () => {
      if (ref.current && level && level > 1 && level < levels.length) {
        ref.current.classList.remove(levels[level]);
      }
    };
  }, [level]);
  useEffect(() => {
    if (scrollAble || maxHeight) {
      ref.current.classList.add('scroll-able');
    }
    return () => {
      if (ref.current && (scrollAble || maxHeight)) {
        ref.current.classList.remove('scroll-able');
      }
    };
  }, [scrollAble, maxHeight]);
  useEffect(() => {
    if (style) {
      Object.assign(ref.current.style, style);
    }
    return () => {
      if (style && ref.current) {
        ref.current.style = null;
      }
    };
  }, [style]);
  const ListView = useMemo(() => jsx("div", {
    css: ListRoot,
    ref: ref,
    className: ('DGN-UI-List ' + className).trim(),
    ...props
  }, children), [children]);
  return ListView;
}));
List.defaultProps = {
  scrollAble: false,
  width: '100%',
  maxWidth: '360px',
  className: ''
};
List.propTypes = {
  /** fixed height of List and positive scroll list items */
  scrollAble: PropTypes.bool,
  /** class for List */
  className: PropTypes.string,
  /** width of list */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** max width */
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** fixed height of List and positive scroll list items */
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** represents the level of nested List, The higher the level, the content will be pushed */
  level: PropTypes.number,
  // [1,2,3,4,5]
  /** the function to get ref */
  refs: PropTypes.func,
  /** style inline for List */
  style: PropTypes.object,
  /** include list subheader, list items */
  children: PropTypes.node
};
export default List;